/**
 * Shared helper for projecting UoW-level dependencies onto subtask-level
 * dependencies. Plan revisions store dependencies on UoWs (a finer granularity
 * than subtasks); each subtask owns one or more UoWs. A subtask depends on
 * another subtask iff one of its UoWs declares a dependency on a UoW owned
 * by that other subtask.
 *
 * Three call sites historically maintained their own copy: the CLI's
 * `deriveSubtaskDependencies`, the orchestrator's
 * `deriveNodeDependenciesFromUows`, and the dashboard's projector. They all
 * adapt their package-specific shapes to the minimal `{id, uowIds}` /
 * `{id, dependencies}` pair this helper accepts.
 */
export interface UowProjectionSubtask {
  id: string;
  uowIds: readonly string[];
}

export interface UowProjectionUow {
  id: string;
  dependencies: readonly string[];
}

export function deriveSubtaskDependenciesFromUows(
  subtasks: readonly UowProjectionSubtask[],
  uows: readonly UowProjectionUow[],
): Map<string, string[]> {
  const result = new Map<string, string[]>();
  if (uows.length === 0) {
    for (const subtask of subtasks) {
      result.set(subtask.id, []);
    }
    return result;
  }

  const uowToOwner = new Map<string, string>();
  for (const subtask of subtasks) {
    for (const uowId of subtask.uowIds) {
      if (!uowToOwner.has(uowId)) {
        uowToOwner.set(uowId, subtask.id);
      }
    }
  }
  const uowDeps = new Map<string, readonly string[]>();
  for (const uow of uows) {
    uowDeps.set(uow.id, uow.dependencies);
  }

  const subtaskIds = new Set(subtasks.map((subtask) => subtask.id));
  for (const subtask of subtasks) {
    const owners = new Set<string>();
    for (const uowId of subtask.uowIds) {
      for (const upstreamUowId of uowDeps.get(uowId) ?? []) {
        const owner = uowToOwner.get(upstreamUowId);
        if (owner && owner !== subtask.id && subtaskIds.has(owner)) {
          owners.add(owner);
        }
      }
    }
    result.set(subtask.id, [...owners]);
  }
  return result;
}
